/**
 * Date utility functions for formatting and parsing dates
 * Used across the application for consistent date handling
 */

function parseDateInput(dateInput: string | number | Date | undefined): Date | null {
  if (dateInput === undefined || dateInput === null || dateInput === '') return null;

  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? null : dateInput;
  }

  if (typeof dateInput === 'number') {
    const date = new Date(dateInput);
    return isNaN(date.getTime()) ? null : date;
  }

  const dateString = String(dateInput).trim();

  const isoDateOnly = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnly) {
    const [, year, month, day] = isoDateOnly;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const europeanDate = dateString.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);
  if (europeanDate) {
    const [, day, month, year] = europeanDate;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

function getDateLocale(lang?: string): string {
  switch ((lang || 'en').toLowerCase()) {
    case 'sk':
      return 'sk-SK';
    case 'cz':
    case 'cs':
      return 'cs-CZ';
    default:
      return 'en-GB';
  }
}

function getDateFormatOptions(lang?: string, includeTime = false): Intl.DateTimeFormatOptions {
  const normalizedLang = (lang || 'en').toLowerCase();
  const options: Intl.DateTimeFormatOptions =
    normalizedLang === 'en'
      ? { day: '2-digit', month: 'short', year: 'numeric' }
      : { day: '2-digit', month: '2-digit', year: 'numeric' };

  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = false;
  }

  return options;
}

/**
 * Formats a date string or timestamp for display in UI using the stable
 * application date convention. Defaults to EN's "DD MMM YYYY" format.
 */
export function formatDateDisplay(dateInput: string | number | Date | undefined, lang?: string): string {
  return formatDateLocalized(dateInput, lang);
}

/**
 * Formats a date and time string or timestamp for display in UI using the
 * same stable locale convention as formatDateDisplay.
 */
export function formatDateTimeDisplay(dateInput: string | number | Date | undefined, lang?: string): string {
  return formatDateTimeLocalized(dateInput, lang);
}

/**
 * Legacy slash based formatter kept for input edge cases. Prefer
 * formatDateLocalized/formatDateDisplay for UI.
 */
export function formatDateDisplayLegacy(dateInput: string | number | Date | undefined): string {
  if (dateInput === undefined || dateInput === null || dateInput === '') return '';

  try {
    // Convert to string based on input type
    let dateStr: string;
    if (typeof dateInput === 'number') {
      dateStr = new Date(dateInput).toISOString();
    } else if (dateInput instanceof Date) {
      dateStr = dateInput.toISOString();
    } else {
      dateStr = String(dateInput);
    }

    // Try to parse the date using the Date object
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      // Format as DD/MM/YYYY
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }

    // Check if date is in YYYY-MM-DD format (from HTML date input)
    if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [year, month, day] = dateInput.split('-');
      return `${day}/${month}/${year}`;
    }

    // Check if date is in DD/MM/YYYY format
    if (typeof dateInput === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateInput)) {
      return dateInput; // Already in the right format
    }

    // Check if date is in DD.MM.YYYY format
    if (typeof dateInput === 'string' && /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateInput)) {
      const [day, month, year] = dateInput.split('.');
      return `${day}/${month}/${year}`;
    }
  } catch (e) {
    console.error('Error formatting date for display:', e);
  }

  // If all else fails, return as is
  return String(dateInput);
}

/**
 * Formats a date string or timestamp for HTML date input (YYYY-MM-DD format)
 * @param dateInput - Date string or timestamp in any supported format
 * @returns Formatted date string in YYYY-MM-DD format for HTML date input
 */
export function formatDateForInput(dateInput: string | number | Date | undefined): string {
  if (dateInput === undefined || dateInput === null || dateInput === '') {
    return '';
  }

  // Convert to string if it's a number
  // Convert to string based on input type
  let dateString: string;
  if (typeof dateInput === 'number') {
    dateString = new Date(dateInput).toISOString();
  } else if (dateInput instanceof Date) {
    dateString = dateInput.toISOString();
  } else {
    dateString = String(dateInput);
  }

  // Check if date is in format DD/MM/YYYY
  if (dateString.includes('/')) {
    const parts = dateString.split('/');
    if (parts.length === 3) {
      // Convert from DD/MM/YYYY to YYYY-MM-DD for input
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
  }

  // Check if date is already in YYYY-MM-DD format
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString;
  }

  // Try to parse as timestamp or ISO date
  try {
    const date = parseDateInput(dateInput);
    if (date && !isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    console.error('Error formatting date for input:', e);
  }

  return '';
}

/**
 * Formats a date string or timestamp for backend submission (YYYY-MM-DD format)
 * @param dateInput - Date string or timestamp in any supported format
 * @returns Formatted date string in YYYY-MM-DD format for API submission
 */
export function formatDateForSubmission(dateInput: string | number | Date | undefined): string {
  if (dateInput === undefined || dateInput === null || dateInput === '') return '';

  try {
    // Create a Date object to handle all formats consistently
    const date = parseDateInput(dateInput);
    
    // Check if date is valid
    if (!date || isNaN(date.getTime())) {
      console.warn('Invalid date input:', dateInput);
      return '';
    }
    
    // Extract year, month, and day components
    const year = date.getFullYear();
    // getMonth() is zero-based, so add 1 and pad with leading zero if needed
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    // Return in YYYY-MM-DD format
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    
    // Fallback handling for string inputs
    if (typeof dateInput === 'string') {
      // If already in YYYY-MM-DD format, return as is
      if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateInput;
      }
      
      // If in DD/MM/YYYY format, convert to YYYY-MM-DD
      if (dateInput.includes('/')) {
        const parts = dateInput.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }
      
      // Try to extract date from ISO string with timezone
      if (dateInput.includes('T')) {
        const datePart = dateInput.split('T')[0];
        if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return datePart;
        }
      }
    }
    
    return '';
  }
}

/**
 * Formats a date string or timestamp for localized display using browser's locale
 * @param dateInput - Date string or timestamp in any supported format
 * @param lang - Optional active app language ('en' | 'sk' | 'cz'). When provided
 *               we use a stable, audit-aligned format per language instead of the
 *               browser's default (which mixes M/D/YYYY for en-US and D.M.YYYY for sk-SK).
 * @returns Localized date string based on user's locale
 */
export function formatDateLocalized(dateInput: string | number | Date | undefined, lang?: string): string {
  if (dateInput === undefined || dateInput === null || dateInput === '') return '';

  try {
    const date = parseDateInput(dateInput);
    if (!date) {
      return String(dateInput);
    }

    return new Intl.DateTimeFormat(getDateLocale(lang), getDateFormatOptions(lang)).format(date);
  } catch (e) {
    console.error('Error formatting localized date:', e);
  }

  return String(dateInput);
}

export function formatDateTimeLocalized(dateInput: string | number | Date | undefined, lang?: string): string {
  if (dateInput === undefined || dateInput === null || dateInput === '') return '';

  try {
    const date = parseDateInput(dateInput);
    if (!date) {
      return String(dateInput);
    }

    return new Intl.DateTimeFormat(getDateLocale(lang), getDateFormatOptions(lang, true)).format(date);
  } catch (e) {
    console.error('Error formatting localized date time:', e);
  }

  return String(dateInput);
}

export function formatMonthLocalized(dateInput: string | number | Date | undefined, lang?: string): string {
  if (dateInput === undefined || dateInput === null || dateInput === '') return '';

  try {
    const date = parseDateInput(dateInput);
    if (!date) {
      return String(dateInput);
    }

    const normalizedLang = (lang || 'en').toLowerCase();
    return new Intl.DateTimeFormat(getDateLocale(lang), {
      month: normalizedLang === 'en' ? 'long' : '2-digit',
      year: 'numeric',
    }).format(date);
  } catch (e) {
    console.error('Error formatting localized month:', e);
  }

  return String(dateInput);
}
